import { PortfolioLabContext, type GetPortfolioLabChartItem } from "$root/functional-areas/portfolio-lab/ctx";
import {
	portfolioLabRepository,
	type PortfolioMetricsConceptsConfig,
} from "$root/functional-areas/portfolio-lab/repository";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import { useQuery } from "@tanstack/react-query";
import type { ColumnState } from "ag-grid-community";
import { useLabSession } from "./useLabSession";

type ReconcileMetricsSeedInput = {
	/** Default concepts the backend config currently offers (system metrics, custom scores, accounting metrics). */
	defaults: ParameterizedConceptDTO[];
	/** The session-level parametrized-concept basket (shared across chart types). */
	basket: ParameterizedConceptDTO[];
	/** THIS chart's series — uuids into the basket. */
	series: string[];
	/** THIS chart's persisted column state (order + visibility). */
	columns: ColumnState[];
};

type ReconcileMetricsSeedResult = {
	basket: ParameterizedConceptDTO[];
	series: string[];
	columns: ColumnState[];
	/** True when a default concept was appended to the basket. */
	basketChanged: boolean;
	/** True when this chart's series or columns changed (append and/or prune). */
	chartChanged: boolean;
};

/**
 * Pure reconcile between the default-concepts config and a persisted session, extracted from
 * {@link useSeedMetrics} so the seed/append/garbage-collect behavior is testable without atoms or
 * react-query. See that hook's docstring for the numbered contract this implements. All diffing is by
 * `conceptId` (never uuid) because the config regenerates uuids on each call.
 */
export function reconcileMetricsSeed({
	defaults,
	basket: existingBasket,
	series: currentSeries,
	columns: currentColumns,
}: ReconcileMetricsSeedInput): ReconcileMetricsSeedResult {
	// 1. Ensure every default concept exists in the session basket, deduping by conceptId so we never
	//    create duplicate parametrized concepts (the config regenerates uuids on each call).
	const basketConceptIds = new Set(existingBasket.map((c) => c.conceptId));
	const missingConcepts = defaults.filter((c) => !basketConceptIds.has(c.conceptId));
	const basket = missingConcepts.length > 0 ? [...existingBasket, ...missingConcepts] : existingBasket;

	// 2. Reconcile the chart's series (data) and columns (view) against the default concepts. Resolve
	//    series/column uuids back to conceptIds (via the basket) so everything diffs by conceptId.
	const uuidByConceptId = new Map(basket.map((c) => [c.conceptId, c.uuid]));
	const conceptIdByUuid = new Map(basket.map((c) => [c.uuid, c.conceptId]));
	const availableConceptIds = new Set(defaults.map((d) => d.conceptId));

	// A series/column entry is stale once its concept is no longer offered by the config (or no longer
	// resolves in the basket at all) — those get garbage-collected below, not merely hidden.
	const isAvailable = (uuid: string | undefined): boolean => {
		const conceptId = uuid != null ? conceptIdByUuid.get(uuid) : undefined;
		return conceptId != null && availableConceptIds.has(conceptId);
	};

	const seriesConceptIds = new Set(currentSeries.map((uuid) => conceptIdByUuid.get(uuid)).filter(Boolean));

	// 2a. Append every default concept not yet in the series, as a new visible column at the end.
	const newSeriesIds: string[] = defaults
		.filter((d) => !seriesConceptIds.has(d.conceptId))
		.map((d) => uuidByConceptId.get(d.conceptId))
		.filter((uuid): uuid is string => uuid != null);
	const newColumns: ColumnState[] = newSeriesIds.map((colId) => ({ colId, hide: false }));

	// 2b. Prune stale entries, then append the new ones — preserving the user's existing order/visibility.
	const series = [...currentSeries.filter(isAvailable), ...newSeriesIds];
	const columns = [...currentColumns.filter((col) => isAvailable(col.colId)), ...newColumns];

	const chartChanged =
		series.length !== currentSeries.length || columns.length !== currentColumns.length || newSeriesIds.length > 0;

	return { basket, series, columns, basketChanged: missingConcepts.length > 0, chartChanged };
}

/**
 * Keeps the TABLE (Portfolio Metrics) chart in sync with the mandatory/default concepts on every load.
 *
 * The lab session is the source of truth for which concepts the table shows (`series` = uuids into the
 * session-level `parametrizedConcepts` basket). This hook fetches the default concepts
 * (`portfolioMetricsConceptsConfig` — system metrics, custom scores, accounting metrics) and reconciles
 * them against the persisted session:
 *
 *   1. Any default concept missing from the basket is appended (with a fresh, stable uuid).
 *   2. Any default concept not yet represented in THIS chart's series is appended as a new visible column
 *      at the end — preserving the user's existing column order and visibility.
 *   3. Any series/column whose concept the config no longer offers (or that no longer resolves in the
 *      basket) is dropped from THIS chart, so retired metrics don't linger as empty ghost columns.
 *   4. The session is persisted only when the reconcile actually changed something, so it converges.
 *
 * This subsumes the first-load bootstrap (an empty series means every default concept is "missing", so all
 * get seeded) and, crucially, lets concepts added on the backend later — e.g. a new accounting metric — show
 * up for users who already have a saved session. Concepts are diffed by `conceptId`, not uuid, because the
 * config regenerates uuids on every call. The settings panel can only hide/reorder columns (never add or
 * remove them), so appending missing concepts and pruning retired ones never fights a user's choices.
 */
export function useSeedMetrics(chartConfig: GetPortfolioLabChartItem<"TABLE">): void {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const { saveLabSession } = useLabSession();

	const seedQuery = useQuery(portfolioLabRepository.portfolioMetricsConceptsConfig.getQueryConfig());

	useQueryReactor(seedQuery, {
		onSuccess({ parametrizedConcepts: defaults }: PortfolioMetricsConceptsConfig) {
			const chart = readAtom(ctx.charts$).find(
				(c): c is GetPortfolioLabChartItem<"TABLE"> => c.type === "TABLE" && c.uuid === chartConfig.uuid,
			);
			const result = reconcileMetricsSeed({
				defaults,
				basket: readAtom(ctx.parametrizedConcepts$),
				series: chart?.series ?? [],
				columns: chart?.preference.columns ?? [],
			});

			if (result.basketChanged) {
				writeAtom(ctx.parametrizedConcepts$, result.basket);
			}
			if (result.chartChanged) {
				writeAtom(ctx.charts$, (charts) =>
					charts.map((c) =>
						c.type === "TABLE" && c.uuid === chartConfig.uuid
							? { ...c, series: result.series, preference: { ...c.preference, columns: result.columns } }
							: c,
					),
				);
			}

			// Persist only when the reconcile changed something — keeps the pass idempotent across reloads.
			if (result.basketChanged || result.chartChanged) {
				saveLabSession();
			}
		},
	});
}
